// src/pages/Accueil.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, addDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import app, { db, serverTimestamp } from "../firebase.js";

/* --- Images du slider --- */
const sliderImages = [
  "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&h=800&fit=crop",
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&h=800&fit=crop",
  "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1200&h=800&fit=crop",
];

/* --- Icônes modernes avec vraies images --- */
const icons = {
  tutors:
    "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop",
  teamwork:
    "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=400&fit=crop",
  environment:
    "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=400&h=400&fit=crop",
  method:
    "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=400&fit=crop",
  feedback:
    "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=400&fit=crop",
  improve:
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&h=400&fit=crop",
};

function Accueil() {
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [popupRole, setPopupRole] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userName, setUserName] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  const auth = getAuth(app);
  const roleSectionRef = useRef(null);
  const formRef = useRef(null);

  // Vérifie la connexion et affiche le popup de bienvenue
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsConnected(!!user);
      setCurrentUser(user || null);

      if (user) {
        const name = user.displayName || user.email?.split("@")[0] || "Utilisateur";
        setUserName(name);
        setShowWelcome(true);

        setTimeout(() => setShowWelcome(false), 4000);
      }
    });
    return () => unsub();
  }, [auth]);

  // Slider auto
  useEffect(() => {
    const id = setInterval(() => {
      setCurrentIndex((p) => (p === sliderImages.length - 1 ? 0 : p + 1));
    }, 4000);
    return () => clearInterval(id);
  }, []);

  // Bouton retour haut
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToRoles = () =>
    roleSectionRef.current?.scrollIntoView({ behavior: "smooth" });

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // Envoi Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();

    const roleUsed = selectedRole || "";
    setPopupRole(roleUsed);

    try {
      await addDoc(collection(db, "soumissions"), {
        nom: (formData.nom || "").trim(),
        email: (formData.email || "").trim(),
        telephone: (formData.telephone || "").trim(),
        matiere: (formData.matiere || "").trim(),
        ville: (formData.ville || "").trim(),
        ecole: (formData.ecole || "").trim(),
        message: (formData.message || "").trim(),
        role: roleUsed,
        createdAT: serverTimestamp(),
        uid: currentUser?.uid || null,
      });

      setSuccessMessage(
        `Merci ${formData.nom || ""} 🎉 Votre demande a bien été envoyée !`
      );

      setFormData({});
      setSelectedRole(null);

      setTimeout(() => {
        setSuccessMessage("");
        setPopupRole(null);
      }, 4000);
    } catch (err) {
      console.error("Erreur Firestore:", err);
      alert("❌ Une erreur est survenue lors de l'enregistrement.");
    }
  };

  const handleSelectRole = (role) => {
    setSelectedRole(role);
    setTimeout(
      () => formRef.current?.scrollIntoView({ behavior: "smooth" }),
      200
    );
  };

  const handleGoBack = () => setSelectedRole(null);

  // ✅ Écran non connecté (ULTRA MODERNISÉ)
  if (!isConnected) {
    return (
      <div style={styles.lockedPage}>
        {/* Animated background blobs */}
        <div style={styles.bgBlob1} />
        <div style={styles.bgBlob2} />
        <div style={styles.bgBlob3} />

        <motion.div
          style={styles.lockedCard}
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Décorations éducatives en arrière-plan de la card */}
          <div style={styles.bgEducationPattern}>
            <motion.div
              style={styles.bgBook1}
              animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              📚
            </motion.div>
            <motion.div
              style={styles.bgBook2}
              animate={{ y: [0, 10, 0], rotate: [0, -5, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              📖
            </motion.div>
            <motion.div
              style={styles.bgGradCap1}
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            >
              🎓
            </motion.div>
            <motion.div
              style={styles.bgGradCap2}
              animate={{ y: [0, 8, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            >
              🎓
            </motion.div>
            <motion.div
              style={styles.bgStar1}
              animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              ⭐
            </motion.div>
            <motion.div
              style={styles.bgStar2}
              animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            >
              ✨
            </motion.div>
            <motion.div
              style={styles.bgStar3}
              animate={{ scale: [1, 1.15, 1], rotate: [0, -180, -360] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            >
              ⭐
            </motion.div>
            <motion.div
              style={styles.bgPencil}
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              ✏️
            </motion.div>
            <motion.div
              style={styles.bgLightbulb}
              animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              💡
            </motion.div>
          </div>

          {/* Header badges */}
          <div style={styles.lockedTopRow}>
            <motion.div
              style={styles.lockedBadge}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <span style={styles.badgeDot} />
              Accès sécurisé
            </motion.div>
            <motion.div
              style={styles.lockedBadgeSoft}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              HDC_Plateforme éducative
            </motion.div>
          </div>

          {/* Icon avec effet de glow animé */}
          <motion.div
            style={styles.lockedIconWrapper}
            animate={{
              boxShadow: [
                "0 20px 60px rgba(102, 126, 234, 0.3)",
                "0 20px 70px rgba(102, 126, 234, 0.5)",
                "0 20px 60px rgba(102, 126, 234, 0.3)",
              ],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.img
              src="https://cdn-icons-png.flaticon.com/512/5957/5957075.png"
              alt="lock"
              style={styles.lockedIcon}
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>

          {/* Title avec gradient animé */}
          <motion.h2
            style={styles.lockedTitle}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Bienvenue sur <span style={styles.gradientText}>HDC Éducation</span> 🎓
          </motion.h2>

          <motion.p
            style={styles.lockedText}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Connecte-toi pour accéder à ton espace (profil, cours, suivi) ou crée
            un compte en 30 secondes.
          </motion.p>

          {/* Action buttons avec effets améliorés */}
          <motion.div
            style={styles.lockedActions}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <motion.button
              type="button"
              style={styles.lockedPrimaryBtn}
              onClick={() => navigate("/connexion")}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <span style={styles.btnIcon}>🔐</span>
              Se connecter
            </motion.button>

            <motion.button
              type="button"
              style={styles.lockedSecondaryBtn}
              onClick={() => navigate("/inscription")}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <span style={styles.btnIcon}>👨‍🎓</span>
              Créer un compte
            </motion.button>
          </motion.div>

          {/* Divider animé */}
          <motion.div
            style={styles.lockedDivider}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <span style={styles.lockedDividerText}>Ce que tu peux faire</span>
          </motion.div>

          {/* Feature cards avec stagger animation */}
          <div style={styles.lockedFeatures}>
            {[
              {
                title: "Trouver un tuteur",
                text: "Choisis une matière, une ville et un créneau.",
                emoji: "🎯",
                color: "rgba(102, 126, 234, 0.1)",
              },
              {
                title: "Suivre ta progression",
                text: "Historique, objectifs, feedback des séances.",
                emoji: "📈",
                color: "rgba(240, 147, 251, 0.1)",
              },
              {
                title: "Espace école",
                text: "Partenariats, coordination et suivi des élèves.",
                emoji: "🏫",
                color: "rgba(79, 172, 254, 0.1)",
              },
            ].map((f, idx) => (
              <motion.div
                key={idx}
                style={styles.lockedFeatureCard}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + idx * 0.1, duration: 0.5 }}
                whileHover={{ y: -4, boxShadow: "0 16px 32px rgba(2,12,27,0.1)" }}
              >
                <div style={{ ...styles.lockedFeatureEmoji, background: f.color }}>
                  {f.emoji}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.lockedFeatureTitle}>{f.title}</div>
                  <div style={styles.lockedFeatureText}>{f.text}</div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom hint */}
          <motion.div
            style={styles.lockedHint}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            💡 Astuce : vous pouvez aussi passer par le menu{" "}
            <span style={styles.lockedBold}>Connexion / Inscription</span>.
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* HERO SECTION - Modernisé */}
      <section style={styles.hero}>
        <div style={styles.heroContainer} className="hero-container">
          <motion.div
            style={styles.heroLeft}
            className="hero-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              style={styles.badge}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <span style={styles.badgeShine} />
              📖 Plateforme éducative nouvelle génération
            </motion.div>

            <h1 style={styles.heroTitle} className="hero-title">
              Bienvenue sur{" "}
              <motion.span
                style={styles.heroTitleGradient}
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              >
                HDC_Éducation
              </motion.span>{" "}
              🎓
            </h1>

            <p style={styles.heroSubtitle} className="hero-subtitle">
              Une plateforme éducative moderne qui connecte élèves, étudiants,
              tuteurs et écoles pour un apprentissage personnalisé.
            </p>

            <div style={styles.heroButtons}>
              <motion.button
                style={styles.primaryBtn}
                onClick={scrollToRoles}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                Découvrir les profils
                <motion.span
                  style={styles.btnArrow}
                  animate={{ y: [0, 3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  ↓
                </motion.span>
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            style={styles.heroRight}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div style={styles.sliderWrapper}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  src={sliderImages[currentIndex]}
                  alt="education"
                  style={styles.sliderImage}
                  initial={{ opacity: 0, scale: 1.1, rotateY: 10 }}
                  animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                  exit={{ opacity: 0, scale: 0.95, rotateY: -10 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                />
              </AnimatePresence>
              <div style={styles.sliderGlow} />
              <div style={styles.sliderDots}>
                {sliderImages.map((_, i) => (
                  <motion.button
                    key={i}
                    aria-label={`Aller à l'image ${i + 1}`}
                    onClick={() => setCurrentIndex(i)}
                    style={{
                      ...styles.dot,
                      ...(i === currentIndex ? styles.dotActive : {}),
                    }}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Popup de confirmation - Modernisé */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            key="popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={styles.popupOverlay}
            onClick={() => {
              setSuccessMessage("");
              setPopupRole(null);
            }}
          >
            <motion.div
              style={styles.popupBox}
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                style={styles.popupIcon}
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.5 }}
              >
                ✅
              </motion.div>
              <h3 style={styles.popupTitle}>Message envoyé avec succès</h3>
              <p style={styles.popupText}>
                {popupRole === "eleve" &&
                  "Votre demande a bien été envoyée à notre équipe pédagogique. Un conseiller vous contactera sous peu."}
                {popupRole === "tuteur" &&
                  "Votre candidature a bien été enregistrée. Notre équipe vous contactera prochainement."}
                {popupRole === "ecole" &&
                  "Votre demande de partenariat a bien été soumise. Nous reviendrons vers vous dans les plus brefs délais."}
                {!popupRole &&
                  "Votre message a bien été envoyé. Merci pour votre confiance !"}
              </p>
              <motion.button
                style={styles.popupBtn}
                onClick={() => {
                  setSuccessMessage("");
                  setPopupRole(null);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Fermer
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popup de bienvenue moderne */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -50 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            style={styles.welcomePopup}
          >
            <motion.div
              style={styles.welcomeIconWrapper}
              animate={{
                rotate: [0, 10, -10, 10, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              👋
            </motion.div>
            <h3 style={styles.welcomeTitle}>
              Bienvenue, <span style={styles.welcomeName}>{userName}</span> !
            </h3>
            <p style={styles.welcomeText}>
              Heureux de te revoir sur HDC Éducation 🎓
            </p>
            <motion.div
              style={styles.welcomeProgress}
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: 4, ease: "linear" }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Choix de profil - Cards modernisées et compactes */}
      <section ref={roleSectionRef} style={styles.roleSection}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 style={styles.sectionTitle}>Choisissez votre profil</h2>
          <p style={styles.sectionSubtitle}>
            Cliquez sur le profil correspondant pour continuer
          </p>
        </motion.div>

        <div style={styles.roleCards}>
          {[
            {
              img: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?auto=format&fit=crop&q=80&w=1000",
              title: "Je suis Élève / Étudiant",
              text: "Trouvez un tuteur et progressez à votre rythme.",
              role: "eleve",
              btn: "Rechercher un tuteur",
              icon: "🎯",
              gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            },
            {
              img: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=800&h=600&fit=crop",
              title: "Je veux devenir Tuteur",
              text: "Rejoignez notre communauté de tuteurs.",
              role: "tuteur",
              btn: "Devenir Tuteur",
              icon: "👨‍🏫",
              gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            },
            {
              img: "https://images.unsplash.com/photo-1562774053-701939374585?w=800&h=600&fit=crop",
              title: "Je représente une École",
              text: "Inscrivez votre établissement et collaborez.",
              role: "ecole",
              btn: "Inscrire mon École",
              icon: "🏫",
              gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                scale: 1.02,
              }}
              style={styles.card}
            >
              <div style={styles.cardImageWrapper}>
                <img
                  src={item.img}
                  alt={item.title}
                  style={styles.cardImage}
                  loading="lazy"
                />
                <motion.div
                  style={{
                    ...styles.cardImageOverlay,
                    background: item.gradient,
                  }}
                  whileHover={{ opacity: 0.7 }}
                  transition={{ duration: 0.3 }}
                />
                <div style={styles.cardIconBadge}>{item.icon}</div>
              </div>
              <div style={styles.cardContent}>
                <h3 style={styles.cardTitle}>{item.title}</h3>
                <p style={styles.cardText}>{item.text}</p>
                <motion.button
                  style={{ ...styles.gradientBtn, background: item.gradient }}
                  onClick={() => handleSelectRole(item.role)}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 12px 32px rgba(102, 126, 234, 0.3)",
                  }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  {item.btn}
                  <span style={styles.btnArrowSmall}>→</span>
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Formulaire dynamique - Design amélioré */}
      <AnimatePresence>
        {selectedRole && (
          <motion.section
            ref={formRef}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={styles.formSection}
          >
            <div style={styles.formHeader}>
              <motion.div
                style={styles.formIconWrapper}
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                {selectedRole === "eleve" && "👨‍🎓"}
                {selectedRole === "tuteur" && "👨‍🏫"}
                {selectedRole === "ecole" && "🏫"}
              </motion.div>
              <h2 style={styles.formTitle}>
                {selectedRole === "eleve" && "Inscription Élève"}
                {selectedRole === "tuteur" && "Candidature Tuteur"}
                {selectedRole === "ecole" && "Partenariat École"}
              </h2>
              <p style={styles.formSubtitle}>
                Veuillez remplir les informations ci-dessous
              </p>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGrid}>
                {[
                  { name: "nom", placeholder: "Nom complet", type: "text" },
                  { name: "email", placeholder: "Adresse e-mail", type: "email" },
                  { name: "telephone", placeholder: "Numéro de téléphone", type: "tel" },
                  selectedRole === "ecole" && {
                    name: "ecole",
                    placeholder: "Nom de l'établissement",
                    type: "text",
                  },
                  (selectedRole === "eleve" || selectedRole === "tuteur") && {
                    name: "matiere",
                    placeholder:
                      selectedRole === "tuteur"
                        ? "Spécialité d'enseignement"
                        : "Matière à apprendre",
                    type: "text",
                  },
                  { name: "ville", placeholder: "Ville / Localité", type: "text" },
                ]
                  .filter(Boolean)
                  .map((field, idx) => (
                    <motion.div
                      key={field.name}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      style={styles.inputWrapper}
                    >
                      <input
                        name={field.name}
                        type={field.type}
                        placeholder={field.placeholder}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        style={styles.input}
                        required
                      />
                    </motion.div>
                  ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={styles.inputWrapper}
              >
                <textarea
                  name="message"
                  placeholder="Vos besoins ou attentes..."
                  value={formData.message || ""}
                  onChange={handleChange}
                  style={styles.textarea}
                />
              </motion.div>

              <motion.div
                style={styles.formActions}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <motion.button
                  type="button"
                  onClick={handleGoBack}
                  style={styles.backBtn}
                  whileHover={{ scale: 1.03, x: -3 }}
                  whileTap={{ scale: 0.98 }}
                >
                  ← Retour
                </motion.button>
                <motion.button
                  type="submit"
                  style={styles.submitBtn}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Envoyer ma demande
                  <span style={styles.btnCheckmark}>✓</span>
                </motion.button>
              </motion.div>
            </form>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Méthode des tuteurs - Layout compact et moderne */}
      <motion.section
        style={styles.methodSection}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
      >
        <div style={styles.methodHeader}>
          <h2 style={styles.sectionTitle}>
            Nos tuteurs, une pédagogie moderne et humaine
          </h2>
          <p style={styles.methodSubtitle}>
            Une approche complète pour garantir votre réussite
          </p>
        </div>

        <div style={styles.methodGrid}>
          {[
            {
              icon: icons.tutors,
              title: "Des tuteurs qualifiés",
              text: "Sélectionnés et formés dans leur pratique éducative.",
              color: "#667eea",
            },
            {
              icon: icons.teamwork,
              title: "Travail d'équipe",
              text: "Collaboration avec parents et enseignants.",
              color: "#f093fb",
            },
            {
              icon: icons.environment,
              title: "Un cadre bienveillant",
              text: "Apprentissage dans un climat de confiance.",
              color: "#4facfe",
            },
            {
              icon: icons.method,
              title: "Une méthode efficace",
              text: "Objectifs, plans d'étude et évaluations.",
              color: "#43e97b",
            },
            {
              icon: icons.feedback,
              title: "Suivi régulier",
              text: "Retour clair après chaque séance.",
              color: "#fa709a",
            },
            {
              icon: icons.improve,
              title: "Amélioration continue",
              text: "Formation permanente et retours d'expérience.",
              color: "#feca57",
            },
          ].map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{
                y: -4,
                scale: 1.01,
              }}
              style={styles.benefitCard}
            >
              <div style={styles.benefitImageWrapper}>
                <img
                  src={b.icon}
                  alt=""
                  style={styles.benefitImage}
                  loading="lazy"
                />
              </div>
              <div style={styles.benefitContent}>
                <h3 style={styles.benefitTitle}>{b.title}</h3>
                <p style={styles.benefitText}>{b.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Retour haut - Design amélioré */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToTop}
            style={styles.scrollTopBtn}
          >
            <motion.span
              animate={{ y: [-2, 2, -2] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              ↑
            </motion.span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

/* --- STYLES MODERNISÉS ET COMPACTS --- */
const styles = {
  // Variables de couleurs (design system)
  colors: {
    primary: "#667eea",
    primaryDark: "#5568d3",
    secondary: "#764ba2",
    text: "#0e1829",
    textLight: "#586274",
    bg: "#f8faff",
    bgLight: "#ffffff",
    border: "rgba(102, 126, 234, 0.12)",
  },

  page: {
    background:
      "radial-gradient(ellipse 1400px 900px at 20% 0%, rgba(102,126,234,0.08) 0%, transparent 60%), radial-gradient(ellipse 1200px 800px at 80% 100%, rgba(118,75,162,0.06) 0%, transparent 50%), linear-gradient(180deg, #ffffff 0%, #f8faff 100%)",
    minHeight: "100vh",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    position: "relative",
  },

  /* ============ LOCKED PAGE - Ultra modern ============ */
  lockedPage: {
    minHeight: "calc(100vh - 140px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 16px",
    position: "relative",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    overflow: "hidden",
  },

  // Animated background blobs
  bgBlob1: {
    position: "absolute",
    top: "-10%",
    right: "-5%",
    width: "600px",
    height: "600px",
    background: "radial-gradient(circle, rgba(255,255,255,0.15), transparent 70%)",
    borderRadius: "50%",
    filter: "blur(60px)",
    animation: "float 20s ease-in-out infinite",
  },
  bgBlob2: {
    position: "absolute",
    bottom: "-10%",
    left: "-5%",
    width: "500px",
    height: "500px",
    background: "radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%)",
    borderRadius: "50%",
    filter: "blur(50px)",
    animation: "float 15s ease-in-out infinite reverse",
  },
  bgBlob3: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "800px",
    height: "800px",
    background: "radial-gradient(circle, rgba(255,255,255,0.08), transparent 70%)",
    borderRadius: "50%",
    filter: "blur(80px)",
    animation: "pulse 10s ease-in-out infinite",
  },

  lockedCard: {
    width: "100%",
    maxWidth: "680px",
    position: "relative",
    background: "rgba(255, 255, 255, 0.92)",
    border: "1px solid rgba(255, 255, 255, 0.5)",
    borderRadius: "24px",
    boxShadow:
      "0 20px 60px rgba(102, 126, 234, 0.3), 0 0 1px rgba(255, 255, 255, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
    padding: "28px 32px",
    backdropFilter: "blur(25px)",
    WebkitBackdropFilter: "blur(25px)",
    zIndex: 10,
  },

  lockedTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "14px",
    flexWrap: "wrap",
  },

  lockedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "999px",
    fontWeight: "700",
    fontSize: "11px",
    color: "#0e1829",
    background: "rgba(102, 126, 234, 0.08)",
    border: "1.5px solid rgba(102, 126, 234, 0.2)",
    boxShadow: "0 2px 6px rgba(102, 126, 234, 0.08)",
  },

  badgeDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#667eea",
    boxShadow: "0 0 8px rgba(102, 126, 234, 0.6)",
    animation: "pulse 2s ease-in-out infinite",
  },

  lockedBadgeSoft: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: "999px",
    fontWeight: "700",
    fontSize: "11px",
    color: "#586274",
    background: "rgba(88, 98, 116, 0.06)",
    border: "1.5px solid rgba(88, 98, 116, 0.1)",
  },

  lockedIconWrapper: {
    width: "80px",
    height: "80px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "12px auto 18px",
    position: "relative",
    overflow: "hidden",
  },

  lockedIcon: {
    width: "44px",
    height: "44px",
    filter: "brightness(0) invert(1)",
    position: "relative",
    zIndex: 2,
  },

  lockedTitle: {
    textAlign: "center",
    fontSize: "clamp(22px, 2.8vw, 28px)",
    fontWeight: "800",
    color: "#0e1829",
    margin: "0 0 10px",
    letterSpacing: "-0.03em",
    lineHeight: "1.2",
  },

  gradientText: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    backgroundSize: "200% 200%",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },

  lockedText: {
    textAlign: "center",
    fontSize: "14px",
    color: "#586274",
    maxWidth: "540px",
    margin: "0 auto 20px",
    lineHeight: "1.5",
  },

  lockedActions: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "24px",
  },

  lockedPrimaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    padding: "11px 22px",
    borderRadius: "12px",
    border: "none",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
    color: "#fff",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    boxShadow:
      "0 6px 20px rgba(102, 126, 234, 0.28), 0 3px 10px rgba(102, 126, 234, 0.18)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    position: "relative",
    overflow: "hidden",
  },

  lockedSecondaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    padding: "11px 22px",
    borderRadius: "12px",
    border: "2px solid rgba(102, 126, 234, 0.2)",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
    color: "#0e1829",
    background: "rgba(255, 255, 255, 0.9)",
    boxShadow: "0 3px 12px rgba(2, 12, 27, 0.05)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  btnIcon: {
    fontSize: "16px",
  },

  lockedDivider: {
    position: "relative",
    margin: "24px 0 18px",
    height: "1px",
    background:
      "linear-gradient(90deg, transparent, rgba(88, 98, 116, 0.2), transparent)",
    transformOrigin: "left",
  },

  lockedDividerText: {
    position: "absolute",
    left: "50%",
    top: "-9px",
    transform: "translateX(-50%)",
    padding: "0 12px",
    background: "rgba(255, 255, 255, 0.98)",
    color: "#586274",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },

  lockedFeatures: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "10px",
  },

  lockedFeatureCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "12px",
    borderRadius: "14px",
    background: "rgba(255, 255, 255, 0.7)",
    border: "1.5px solid rgba(230, 236, 255, 0.8)",
    boxShadow: "0 3px 12px rgba(2, 12, 27, 0.04)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  lockedFeatureEmoji: {
    width: "36px",
    height: "36px",
    minWidth: "36px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    border: "1.5px solid rgba(102, 126, 234, 0.15)",
    transition: "all 0.3s ease",
  },

  lockedFeatureTitle: {
    fontWeight: "700",
    fontSize: "13px",
    color: "#0e1829",
    marginBottom: "3px",
    letterSpacing: "-0.01em",
  },

  lockedFeatureText: {
    color: "#586274",
    fontSize: "12px",
    lineHeight: "1.4",
  },

  lockedHint: {
    marginTop: "18px",
    textAlign: "center",
    color: "#586274",
    fontSize: "12px",
    lineHeight: "1.4",
  },

  lockedBold: {
    color: "#667eea",
    fontWeight: "700",
  },

  // Éléments éducatifs en arrière-plan de la card
  bgEducationPattern: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    overflow: "hidden",
    borderRadius: "24px",
    opacity: 0.4,
  },

  bgBook1: {
    position: "absolute",
    top: "8%",
    left: "5%",
    fontSize: "32px",
    opacity: 0.25,
    filter: "blur(0.5px)",
  },

  bgBook2: {
    position: "absolute",
    bottom: "12%",
    right: "8%",
    fontSize: "28px",
    opacity: 0.2,
    filter: "blur(0.5px)",
  },

  bgGradCap1: {
    position: "absolute",
    top: "15%",
    right: "10%",
    fontSize: "36px",
    opacity: 0.2,
    filter: "blur(0.5px)",
  },

  bgGradCap2: {
    position: "absolute",
    bottom: "8%",
    left: "8%",
    fontSize: "30px",
    opacity: 0.25,
    filter: "blur(0.5px)",
  },

  bgStar1: {
    position: "absolute",
    top: "25%",
    left: "15%",
    fontSize: "20px",
    opacity: 0.3,
  },

  bgStar2: {
    position: "absolute",
    top: "45%",
    right: "12%",
    fontSize: "24px",
    opacity: 0.3,
  },

  bgStar3: {
    position: "absolute",
    bottom: "35%",
    left: "10%",
    fontSize: "18px",
    opacity: 0.3,
  },

  bgPencil: {
    position: "absolute",
    top: "50%",
    left: "5%",
    fontSize: "26px",
    opacity: 0.2,
    filter: "blur(0.5px)",
  },

  bgLightbulb: {
    position: "absolute",
    top: "35%",
    right: "5%",
    fontSize: "28px",
    opacity: 0.4,
  },

  /* ============ HERO SECTION ============ */
  hero: {
    padding: "60px 6% 50px",
    background: "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
    position: "relative",
    overflow: "hidden",
  },

  heroContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "60px",
    maxWidth: "1200px",
    margin: "0 auto",
    alignItems: "center",
    "@media (max-width: 968px)": {
      gridTemplateColumns: "1fr",
      gap: "40px",
    },
  },

  heroLeft: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
  },

  heroRight: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    padding: "7px 16px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "700",
    marginBottom: "20px",
    boxShadow:
      "0 4px 16px rgba(102, 126, 234, 0.25), 0 2px 8px rgba(102, 126, 234, 0.12)",
    position: "relative",
    overflow: "hidden",
  },

  badgeShine: {
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
    animation: "shine 3s ease-in-out infinite",
  },

  heroTitle: {
    fontSize: "clamp(30px, 4vw, 42px)",
    fontWeight: "800",
    lineHeight: "1.2",
    color: "#0e1829",
    marginBottom: "16px",
    letterSpacing: "-0.03em",
  },

  heroTitleGradient: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    backgroundSize: "200% 200%",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    display: "inline-block",
  },

  heroSubtitle: {
    color: "#586274",
    fontSize: "clamp(14px, 1.8vw, 16px)",
    maxWidth: "500px",
    lineHeight: "1.6",
    marginBottom: "28px",
  },

  heroButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },

  primaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    padding: "13px 28px",
    borderRadius: "12px",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
    boxShadow:
      "0 6px 20px rgba(102, 126, 234, 0.28), 0 3px 10px rgba(102, 126, 234, 0.15)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  btnArrow: {
    display: "inline-block",
    fontSize: "16px",
  },

  /* ============ SLIDER ============ */
  sliderWrapper: {
    position: "relative",
    width: "100%",
    maxWidth: "500px",
  },

  sliderGlow: {
    position: "absolute",
    inset: "-20px",
    borderRadius: "28px",
    background:
      "radial-gradient(closest-side, rgba(102, 126, 234, 0.15), transparent)",
    filter: "blur(15px)",
    zIndex: 0,
    pointerEvents: "none",
  },

  sliderImage: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "auto",
    aspectRatio: "4/3",
    borderRadius: "20px",
    objectFit: "cover",
    boxShadow:
      "0 20px 50px rgba(2, 12, 27, 0.12), 0 0 1px rgba(2, 12, 27, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.9)",
  },

  sliderDots: {
    display: "flex",
    justifyContent: "center",
    gap: "7px",
    marginTop: "20px",
  },

  dot: {
    width: "8px",
    height: "8px",
    border: "none",
    padding: 0,
    borderRadius: "999px",
    background: "rgba(102, 126, 234, 0.25)",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  dotActive: {
    width: "28px",
    borderRadius: "999px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
  },

  /* ============ ROLE SECTION - ULTRA COMPACT ET MODERNE ============ */
  roleSection: {
    background: "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
    padding: "60px 6%",
    position: "relative",
  },

  sectionTitle: {
    textAlign: "center",
    fontSize: "clamp(26px, 3vw, 32px)",
    fontWeight: "800",
    color: "#0e1829",
    marginBottom: "10px",
    letterSpacing: "-0.03em",
  },

  sectionSubtitle: {
    textAlign: "center",
    color: "#586274",
    marginBottom: "40px",
    fontSize: "14px",
    lineHeight: "1.5",
  },

  roleCards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "24px",
    maxWidth: "1100px",
    margin: "0 auto",
  },

  card: {
    background: "rgba(255, 255, 255, 0.98)",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow:
      "0 8px 28px rgba(2, 12, 27, 0.06), 0 0 1px rgba(2, 12, 27, 0.04)",
    transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
    border: "1px solid rgba(233, 240, 255, 0.9)",
    backdropFilter: "blur(10px)",
  },

  cardImageWrapper: {
    position: "relative",
    width: "100%",
    height: "160px",
    overflow: "hidden",
  },

  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scale(1.05)",
    transition: "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  cardImageOverlay: {
    position: "absolute",
    inset: 0,
    opacity: 0.8,
    mixBlendMode: "multiply",
    transition: "opacity 0.3s ease",
  },

  cardIconBadge: {
    position: "absolute",
    top: "14px",
    right: "14px",
    width: "44px",
    height: "44px",
    borderRadius: "14px",
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    boxShadow: "0 6px 20px rgba(2, 12, 27, 0.12)",
  },

  cardContent: {
    padding: "20px 18px",
  },

  cardTitle: {
    margin: "0 0 8px 0",
    color: "#0e1829",
    fontSize: "17px",
    fontWeight: "800",
    letterSpacing: "-0.02em",
  },

  cardText: {
    color: "#586274",
    minHeight: "40px",
    lineHeight: "1.5",
    fontSize: "13px",
    marginBottom: "16px",
  },

  gradientBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    color: "#fff",
    border: "none",
    padding: "11px 20px",
    borderRadius: "12px",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
    boxShadow:
      "0 6px 20px rgba(102, 126, 234, 0.24), 0 3px 10px rgba(102, 126, 234, 0.12)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    width: "100%",
  },

  btnArrowSmall: {
    fontSize: "16px",
    transition: "transform 0.3s ease",
  },

  /* ============ FORM SECTION ============ */
  formSection: {
    marginTop: "40px",
    marginBottom: "30px",
    background: "rgba(255, 255, 255, 0.98)",
    padding: "32px",
    borderRadius: "20px",
    width: "92%",
    maxWidth: "900px",
    marginInline: "auto",
    boxShadow:
      "0 16px 48px rgba(2, 12, 27, 0.08), 0 0 1px rgba(2, 12, 27, 0.04)",
    border: "1px solid rgba(233, 240, 255, 1)",
    backdropFilter: "blur(12px)",
  },

  formHeader: {
    textAlign: "center",
    marginBottom: "28px",
  },

  formIconWrapper: {
    width: "52px",
    height: "52px",
    margin: "0 auto 12px",
    fontSize: "26px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
    borderRadius: "14px",
    border: "2px solid rgba(102, 126, 234, 0.2)",
  },

  formTitle: {
    color: "#0e1829",
    fontSize: "22px",
    fontWeight: "800",
    marginBottom: "8px",
    letterSpacing: "-0.02em",
  },

  formSubtitle: {
    color: "#586274",
    fontSize: "13px",
    lineHeight: "1.5",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "12px",
  },

  inputWrapper: {
    position: "relative",
  },

  input: {
    width: "100%",
    padding: "11px 14px",
    borderRadius: "10px",
    border: "2px solid rgba(102, 126, 234, 0.15)",
    outline: "none",
    fontSize: "13px",
    fontWeight: "500",
    color: "#0e1829",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.8)",
  },

  textarea: {
    width: "100%",
    height: "100px",
    padding: "11px 14px",
    borderRadius: "10px",
    border: "2px solid rgba(102, 126, 234, 0.15)",
    outline: "none",
    fontSize: "13px",
    fontWeight: "500",
    color: "#0e1829",
    resize: "vertical",
    fontFamily: "inherit",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.8)",
  },

  formActions: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: "8px",
  },

  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
    color: "#fff",
    border: "none",
    padding: "11px 22px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
    boxShadow: "0 4px 16px rgba(245, 101, 101, 0.2)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    padding: "11px 26px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
    boxShadow:
      "0 4px 16px rgba(102, 126, 234, 0.22), 0 2px 8px rgba(102, 126, 234, 0.1)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  btnCheckmark: {
    fontSize: "14px",
  },

  /* ============ METHOD SECTION - LAYOUT HORIZONTAL COMPACT ============ */
  methodSection: {
    padding: "60px 8% 70px",
    background: "#ffffff",
  },

  methodHeader: {
    textAlign: "center",
    maxWidth: "750px",
    margin: "0 auto 50px",
  },

  methodSubtitle: {
    color: "#586274",
    fontSize: "15px",
    lineHeight: "1.6",
    marginTop: "10px",
  },

  methodGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "32px 24px",
    maxWidth: "1200px",
    margin: "0 auto",
  },

  benefitCard: {
    display: "flex",
    alignItems: "center",
    gap: "18px",
    background: "transparent",
    padding: "0",
    borderRadius: "0",
    border: "none",
    boxShadow: "none",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  benefitImageWrapper: {
    position: "relative",
    width: "80px",
    height: "80px",
    minWidth: "80px",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 6px 20px rgba(2, 12, 27, 0.08)",
  },

  benefitImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  benefitContent: {
    flex: 1,
    textAlign: "left",
  },

  benefitTitle: {
    margin: "0 0 6px 0",
    color: "#0e1829",
    fontWeight: "800",
    fontSize: "16px",
    letterSpacing: "-0.02em",
  },

  benefitText: {
    color: "#586274",
    fontSize: "14px",
    lineHeight: "1.5",
    margin: 0,
  },

  /* ============ POPUP ============ */
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(14, 24, 41, 0.7)",
    backdropFilter: "blur(8px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },

  popupBox: {
    background: "rgba(255, 255, 255, 0.98)",
    padding: "32px",
    borderRadius: "20px",
    textAlign: "center",
    maxWidth: "420px",
    boxShadow:
      "0 20px 60px rgba(0, 0, 0, 0.25), 0 0 1px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(20px)",
  },

  popupIcon: {
    fontSize: "48px",
    marginBottom: "14px",
  },

  popupTitle: {
    fontSize: "20px",
    fontWeight: "800",
    color: "#0e1829",
    marginBottom: "10px",
    letterSpacing: "-0.02em",
  },

  popupText: {
    fontSize: "13px",
    color: "#586274",
    lineHeight: "1.5",
    marginBottom: "22px",
  },

  popupBtn: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    padding: "11px 28px",
    borderRadius: "10px",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
    boxShadow:
      "0 4px 16px rgba(102, 126, 234, 0.22), 0 2px 8px rgba(102, 126, 234, 0.1)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  /* ============ WELCOME POPUP ============ */
  welcomePopup: {
    position: "fixed",
    top: "24px",
    right: "24px",
    background:
      "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 255, 0.98) 100%)",
    backdropFilter: "blur(20px)",
    padding: "20px 24px",
    borderRadius: "16px",
    boxShadow:
      "0 12px 40px rgba(102, 126, 234, 0.2), 0 0 1px rgba(102, 126, 234, 0.1)",
    border: "1px solid rgba(102, 126, 234, 0.1)",
    zIndex: 10000,
    maxWidth: "340px",
    overflow: "hidden",
  },

  welcomeIconWrapper: {
    fontSize: "32px",
    marginBottom: "8px",
    display: "inline-block",
  },

  welcomeTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0e1829",
    marginBottom: "6px",
    letterSpacing: "-0.01em",
  },

  welcomeName: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontWeight: "800",
  },

  welcomeText: {
    fontSize: "13px",
    color: "#586274",
    lineHeight: "1.4",
    marginBottom: "12px",
  },

  welcomeProgress: {
    position: "absolute",
    bottom: 0,
    left: 0,
    height: "3px",
    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "0 0 16px 16px",
  },

  /* ============ SCROLL TO TOP ============ */
  scrollTopBtn: {
    position: "fixed",
    bottom: "28px",
    right: "28px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "44px",
    height: "44px",
    fontSize: "20px",
    cursor: "pointer",
    boxShadow:
      "0 8px 24px rgba(102, 126, 234, 0.28), 0 3px 10px rgba(102, 126, 234, 0.15)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

// Ajout des keyframes pour les animations CSS
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes float {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(30px, -30px); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.8; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.05); }
  }

  @keyframes shine {
    0% { left: -100%; }
    100% { left: 200%; }
  }

  /* Responsive Hero Grid */
  @media (max-width: 968px) {
    .hero-container {
      grid-template-columns: 1fr !important;
      gap: 40px !important;
    }

    .hero-left {
      align-items: center !important;
      text-align: center !important;
    }

    .hero-title, .hero-subtitle {
      text-align: center;
    }

    .hero-subtitle {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

if (!document.getElementById("accueil-page-styles")) {
  styleSheet.id = "accueil-page-styles";
  document.head.appendChild(styleSheet);
}

export default Accueil;